/**
 * The Export dialog (W2-09 publish-import; DESIGN.md §4.16, §5.11; DIALOG.exportDialog): Ctrl+E opens it with the
 * last settings and, in quick mode, the Export button focused, so Ctrl+E then Enter exports.
 *
 *   File [Title [Level].pez]  Save to (o) Downloads ( ) Choose where to save (Chromium)
 *   Export as: package / chart file only / project backup
 *   Preflight: blocking problems (they turn Export into "Export anyway...", which asks first) and conversion notes;
 *              opening the dialog starts a full checker pass, shown as "Checking the chart..." while it runs
 *   Written: format, RPEVersion, info.yml switches        Output: files and sizes (missing files are not written)
 *   Chart file only: "Phira will play this differently without info.yml" + "Bake speed to linear for this file"
 *   (for another target the same list is shown for "Copy chart.json" whenever the copied chart would play
 *   differently, so it is in front of the user before anything is copied)
 *   [Copy chart.json]                                                     [Cancel] [Export]
 *
 * Export = clearance (a running checker pass is waited for, briefly; blocking problems as they are then, or a pass
 * that did not finish, ask "Export anyway" first) -> destination (still within the user action, for the save
 * dialog) -> flush -> io worker -> strict self-check -> write -> record the export + version "Exported" -> toast
 * with the Phira import hint. A failed self-check (an exporter bug) shows the differences and saves only after
 * "Save anyway" is confirmed; an empty result is never saved. "Copy chart.json" asks the same "Export anyway"
 * confirmation when the chart file alone has blocking problems.
 *
 * Enter anywhere in the dialog body starts the primary action: in the file name, on the target radios, the
 * destination choice and the checkboxes (as in a form). Buttons and links in the body keep Enter for themselves.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { DialogProps } from '../../app/registry/types';
import type { AppServices } from '../../app/registry/types';
import type { ExportProfile } from '../../core/model/types';
import type { ExportTarget } from '../../formats/types';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { ProgressBar } from '../../ui/ProgressBar';
import { Segmented } from '../../ui/Segmented';
import { Select } from '../../ui/Select';
import { TextField } from '../../ui/TextField';
import type { DocStore } from '../../state/types';
import { ChartOnlyNotice } from './ChartOnlyNotice';
import { errorText, runCopyChart } from './copyChart';
import { IssueRow } from './CheckSummary';
import {
  cleanFileName,
  copyText,
  defaultFileName,
  exportedToast,
  exportSaver,
  exportSpeedMode,
  exportVerdict,
  missingFiles,
  plannedFiles,
  produceExport,
  recordExport,
  withEstimates,
  type PlannedFile,
  type ProducedExport,
  type SaveHandle,
} from './exportFlow';
import { clearForExport } from './clearance';
import { useDeferred, useStore, useText, type Text } from './hooks';
import { usePublishModel, type PublishModel } from './model';
import { formatBytes } from './preflight';
import { writePref, type Destination, type PublishTarget } from './prefs';
import { patchPkg, TARGET_TEXT } from './PublishWorkspace';
import { m } from './publish.messages';
import s from './Publish.module.css';
import { failedFileName, isBytesMissing } from './storedFiles';

type Phase =
  | { readonly kind: 'ready' }
  /** Waiting for the checker pass that was running when Export was pressed. */
  | { readonly kind: 'checking' }
  | { readonly kind: 'running'; readonly progress: number | null }
  /** A failed self-check, or (`warned`) a correct file Phira plays differently (ExportResult.warnings). */
  | {
      readonly kind: 'checkFailed';
      readonly produced: ProducedExport;
      readonly handle: SaveHandle;
      readonly warned: boolean;
    }
  | { readonly kind: 'empty'; readonly diffs: readonly string[] }
  | { readonly kind: 'error'; readonly message: string };

function Preflight(props: { readonly services: AppServices; readonly text: Text; readonly model: PublishModel }) {
  const { services, text, model } = props;
  const notes = model.conversion?.notes ?? [];
  const warns = (model.conversion?.overrideWarning ?? null) !== null;
  const blocking = model.blocking.length;
  const chartOnly = model.options.target === 'chartJson';
  return (
    <section className={s.dialogSection} aria-labelledby="export-preflight">
      <h3 id="export-preflight" className={s.sectionLabel}>
        {text.t(m.preflight)}
      </h3>
      <div className={s.preflightCounts}>
        <span className={s.preflightCount} data-kind="blocking" data-zero={String(blocking === 0)}>
          <Icon name={blocking === 0 ? 'CircleCheck' : 'OctagonX'} size={16} />
          {text.t(m.blockingCount, { count: blocking })}
        </span>
        {/* Conversion notes are information (padded lines, baked speed); only a change of how Phira plays the
            chart (the RPE version override) gets the warning icon (UX-077). */}
        <span className={s.preflightCount} data-kind="notes" data-warn={String(warns)}>
          <Icon name={warns ? 'TriangleAlert' : notes.length === 0 ? 'CircleCheck' : 'Info'} size={16} />
          {model.conversion === null ? text.t(m.calculatingNotes) : text.t(m.notesCount, { count: notes.length })}
        </span>
        <span className={s.preflightCount} data-kind="checking" role="status">
          {model.problems.checking && (
            <>
              <Icon name="LoaderCircle" size={16} />
              {text.t(m.checkingChart)}
            </>
          )}
        </span>
      </div>
      {chartOnly && <span className={s.muted}>{text.t(m.chartOnlyScope)}</span>}
      {blocking > 0 && (
        <ul className={s.issueList} aria-label={text.t(m.blockingTitle)}>
          {model.blocking.map((issue) => (
            <IssueRow key={issue.id} services={services} text={text} issue={issue} />
          ))}
        </ul>
      )}
      {notes.length > 0 && (
        <ul className={s.noteList} aria-label={text.t(m.notesCount, { count: notes.length })}>
          {notes.map((n, i) => (
            <li key={`${n.key}:${String(i)}`}>{text.tr(n)}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** What an export holds, in words ("the chart, the song, the picture and 3 more files"), in the UI language. */
export function fileKinds(
  files: readonly PlannedFile[],
  doc: Pick<PublishModel['store']['doc'], 'meta'>,
  text: Pick<Text, 't' | 'locale'>,
): string {
  const out: string[] = [];
  if (files.some((f) => f.kind === 'chart')) out.push(text.t(m.kindChart));
  const assets = files.filter((f) => f.kind === 'asset').length;
  let named = 0;
  if (assets > named && doc.meta.music !== null) {
    out.push(text.t(m.kindSong));
    named++;
  }
  if (assets > named && doc.meta.illustration !== null) {
    out.push(text.t(m.kindPicture));
    named++;
  }
  if (assets > named) out.push(text.t(m.kindOthers, { count: assets - named }));
  if (files.some((f) => f.kind === 'sidecar')) out.push(text.t(m.kindSidecar));
  try {
    return new Intl.ListFormat(text.locale, { type: 'conjunction' }).format(out);
  } catch {
    return out.join(', ');
  }
}

/**
 * What will be written. A plain summary ("Writes the chart, the song and the picture, about 3.3 MB") and, under
 * Details (open at Full level), the written format switches and the file list (UX-077). Sizes of the chart and
 * phiedit.json come from the project's last export in this session, never from encoding them here (UX-079).
 */
function Output(props: { readonly text: Text; readonly model: PublishModel; readonly full: boolean }) {
  const { text, model } = props;
  const { store, options, conversion } = model;
  const compute = useCallback(
    () =>
      conversion === null
        ? null
        : withEstimates(store.projectId, plannedFiles(store.doc, store.idx, options, conversion.lowered, false)),
    [store, options, conversion],
  );
  const files = useDeferred(compute);
  const missing = missingFiles(store.doc, store.idx);
  const chartOnly = options.target === 'chartJson';
  const version =
    options.rpeVersionOverride ?? conversion?.lowered.chart.META.rpeVersionEffective ?? store.doc.pkg.rpeVersion;
  const written = chartOnly
    ? text.t(m.writtenChartOnly, { version })
    : text.t(m.writtenLine, {
        version,
        speed: String(exportSpeedMode(store.doc, options) === 'rpe170'),
        fix: String(store.doc.pkg.useAttachUiFix),
      });
  const known = files?.every((f) => f.bytes !== null) === true;
  const total = files === null ? 0 : files.reduce((sum, f) => sum + (f.bytes ?? 0), 0);
  const summary =
    files === null
      ? text.t(m.calculating)
      : text.t(known ? m.outputSummary : m.outputSummaryNoSize, {
          files: fileKinds(files, store.doc, text),
          size: formatBytes(total, text),
        });
  return (
    <section className={s.dialogSection} aria-labelledby="export-output">
      <h3 id="export-output" className={s.sectionLabel}>
        {text.t(m.output)}
      </h3>
      <span data-testid="export-summary">{summary}</span>
      {missing.length > 0 && (
        <span className={s.muted}>{text.t(m.missingNotWritten, { files: missing.join(', ') })}</span>
      )}
      <details className={s.disclosure} open={props.full}>
        <summary>{text.t(m.details)}</summary>
        <div className={s.disclosureBody}>
          <span className={s.sectionLabel}>{text.t(m.written)}</span>
          <span className={s.mono}>{written}</span>
          <span className={s.sectionLabel}>{text.t(m.filesLabel)}</span>
          {files === null ? (
            <span className={s.muted}>{text.t(m.calculating)}</span>
          ) : (
            <ul className={s.fileList} aria-label={text.t(m.output)}>
              {files.map((f) => (
                <li key={f.path}>
                  {f.bytes === null
                    ? text.t(m.outputFileUnknown, { name: f.path })
                    : text.t(f.estimated === true ? m.outputFileEstimated : m.outputFile, {
                        name: f.path,
                        size: formatBytes(f.bytes, text),
                      })}
                </li>
              ))}
            </ul>
          )}
          {!known && files !== null && <span className={s.muted}>{text.t(m.sizesAfterExport)}</span>}
        </div>
      </details>
    </section>
  );
}

/** The text of an export failure: a file the browser lost in plain words (UX-066), else the failure's own text. */
export function exportFailureText(e: unknown, text: Pick<Text, 't'>): string {
  const raw = errorText(e, text.t(m.errorTitle));
  if (!isBytesMissing(raw)) return raw;
  const name = failedFileName(raw);
  return name === null ? text.t(m.exportFileLostAny) : text.t(m.exportFileLost, { name });
}

function Failure(props: {
  readonly services: AppServices;
  readonly text: Text;
  readonly title: string;
  readonly body: string;
  readonly diffs: readonly string[];
}) {
  const { services, text } = props;
  const details = props.diffs.join('\n');
  return (
    <div className={s.failure} role="alert">
      <Icon name="CircleX" size={18} />
      <div>
        <p className={s.failureTitle}>{props.title}</p>
        <span>{props.body}</span>
        {details !== '' && (
          <>
            <span className={s.sectionLabel}>{text.t(m.details)}</span>
            <pre className={s.diffs}>{details}</pre>
            <Button
              size="sm"
              icon="Copy"
              onClick={() => {
                void copyText(details).then((ok) => {
                  services.ui.toast(
                    { key: ok ? m.toastDetailsCopied : m.toastCopyFailed },
                    { kind: ok ? 'info' : 'error' },
                  );
                });
              }}
            >
              {text.t(m.copyDetails)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** Roles of choices that take Enter as "submit", like the radios and checkboxes of a form. */
const CHOICE_ROLES: ReadonlySet<string> = new Set(['radio', 'checkbox', 'switch']);

/** Controls that act on Enter themselves (activate, open, insert a line break). */
const OWN_ENTER =
  'button, a[href], textarea, select, summary, [contenteditable=""], [contenteditable="true"], ' +
  '[role="button"], [role="link"], [role="menuitem"], [role="option"], [role="tab"], [role="combobox"]';

/**
 * Whether Enter on `el` (inside the dialog body `body`) starts the primary action: on the body itself, in a text
 * field (it commits its text on Enter, which prevents the default), on radios, checkboxes and switches (native ones
 * and the role-based Segmented and checkbox controls, which do nothing on Enter by design), and on anything else
 * that did not handle Enter. Buttons, links and other controls that act on Enter keep it.
 */
export function entersPrimary(el: HTMLElement, body: HTMLElement, defaultPrevented: boolean): boolean {
  if (el === body) return !defaultPrevented;
  if (el instanceof HTMLInputElement) {
    if (el.type === 'text' || el.type === 'search' || el.type === 'radio' || el.type === 'checkbox') return true;
    return !defaultPrevented && el.type !== 'button' && el.type !== 'submit' && el.type !== 'reset';
  }
  const role = el.getAttribute('role');
  if (role !== null && CHOICE_ROLES.has(role)) return true;
  if (defaultPrevented) return false;
  const own = el.closest(OWN_ENTER);
  return own === null || !body.contains(own);
}

function ExportDialogBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly quick: boolean;
  readonly close: (result: { readonly fileName: string } | null) => void;
}) {
  const { services, store, quick, close } = props;
  const text = useText(services);
  const model = usePublishModel(services, store, 'dialog');
  const saver = useMemo(() => exportSaver(), []);
  const [phase, setPhase] = useState<Phase>({ kind: 'ready' });
  const [typedName, setTypedName] = useState<{ readonly target: ExportTarget; readonly name: string } | null>(null);
  const exportRef = useRef<HTMLButtonElement>(null);
  const alive = useRef(true);
  /** Closed while the worker was exporting: the result is thrown away. */
  const abandoned = useRef(false);
  useEffect(() => {
    // Set again on every mount (StrictMode mounts, unmounts and mounts again).
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const target = model.prefs.target;
  // Technical details (format switches, file list) are open at Full level, folded at Guided (UX-077).
  const [full] = useState(() => services.settings.get('general', 'level') === 'full');
  const destination: Destination = saver.canChoose ? model.prefs.destination : 'download';
  const untitled = text.t(m.untitled);
  const defaultName = defaultFileName(store.doc, target, untitled);
  const shownName = typedName !== null && typedName.target === target ? typedName.name : defaultName;
  const blocking = model.blocking.length;
  const busy = phase.kind === 'running' || phase.kind === 'checking';
  const busyReason = phase.kind === 'checking' ? { key: m.waitingForCheck } : { key: m.running };

  // The preflight has to see what only the checker computes (text panics, zero-delay GIF frames, file names...):
  // opening the dialog starts a full pass (once per dialog; StrictMode runs the effect twice).
  const checked = useRef(false);
  useEffect(() => {
    if (checked.current) return;
    checked.current = true;
    services.problems.fullCheck();
  }, [services]);

  // Quick export (Ctrl+E): the Export button has focus once the dialog has placed its initial focus.
  useEffect(() => {
    if (!quick) return undefined;
    const timer = setTimeout(() => {
      exportRef.current?.focus();
    }, 0);
    return () => {
      clearTimeout(timer);
    };
  }, [quick]);

  /** The dialog was cancelled or unmounted (an export or copy in flight is then dropped). */
  const gone = (): boolean => abandoned.current || !alive.current;

  const set = (next: Phase): void => {
    if (alive.current) setPhase(next);
  };

  const save = async (produced: ProducedExport, handle: SaveHandle): Promise<void> => {
    try {
      await handle.write(produced.result.bytes, produced.result.mime);
    } catch (e) {
      await handle.discard();
      services.ui.toast({ key: m.toastSaveFailed, params: { error: errorText(e, '') } }, { kind: 'error' });
      set({ kind: 'error', message: errorText(e, text.t(m.errorTitle)) });
      return;
    }
    await recordExport(services, store, {
      result: produced.result,
      seq: produced.seq,
      fileName: handle.name,
      at: Date.now(),
    });
    const size = formatBytes(produced.result.bytes.length, text);
    services.ui.toast(exportedToast(target, handle.name, produced.result.bytes.length, size), { kind: 'success' });
    if (produced.unsaved) services.ui.toast({ key: m.unsavedWarning }, { kind: 'warning' });
    close({ fileName: handle.name });
  };

  const runExport = async (): Promise<void> => {
    if (busy || phase.kind === 'checkFailed') return;
    const cleared = await clearForExport(services, store, model.options.target === 'chartJson', {
      onWait: () => {
        set({ kind: 'checking' });
      },
      onWaitEnd: () => {
        set({ kind: 'ready' });
      },
      cancelled: gone,
    });
    if (!cleared || gone()) return;
    const fileName = cleanFileName(shownName, target, defaultName);
    let handle: SaveHandle | null;
    try {
      handle = await saver.begin(destination, fileName, target, text.t(m.savePickerDescription, { target }));
    } catch (e) {
      set({ kind: 'error', message: errorText(e, text.t(m.errorTitle)) });
      return;
    }
    if (handle === null) return;
    set({ kind: 'running', progress: null });
    let produced: ProducedExport;
    try {
      produced = await produceExport(services, store, model.options, (p) => {
        set({ kind: 'running', progress: p });
      });
    } catch (e) {
      await handle.discard();
      set({ kind: 'error', message: exportFailureText(e, text) });
      return;
    }
    if (gone()) {
      await handle.discard();
      return;
    }
    const verdict = exportVerdict(produced.result);
    if (verdict === 'empty') {
      await handle.discard();
      set({ kind: 'empty', diffs: produced.result.selfCheck.diffs });
      return;
    }
    if (verdict === 'selfCheckFailed' || verdict === 'warned') {
      set({ kind: 'checkFailed', produced, handle, warned: verdict === 'warned' });
      return;
    }
    await save(produced, handle);
  };

  const saveAnyway = async (): Promise<void> => {
    if (phase.kind !== 'checkFailed') return;
    const ok = await services.ui.confirm({
      title: { key: phase.warned ? m.saveWarnedTitle : m.saveAnywayTitle },
      body: { key: phase.warned ? m.saveWarnedBody : m.saveAnywayBody },
      confirm: { key: m.saveAnywayConfirm },
      danger: true,
    });
    if (!ok) return;
    set({ kind: 'running', progress: 1 });
    await save(phase.produced, phase.handle);
  };

  const cancel = (): void => {
    abandoned.current = true;
    if (phase.kind === 'checkFailed') void phase.handle.discard();
    close(null);
  };

  /** Runs synchronously up to the clipboard write (it has to start during the click). */
  const copy = async (): Promise<void> => {
    if (busy) return;
    // The wait for a running checker pass starts synchronously, inside runCopyChart.
    const wait = { active: false };
    const running = runCopyChart(services, store, model, true, {
      onWait: () => {
        wait.active = true;
        set({ kind: 'checking' });
      },
      onWaitEnd: () => {
        wait.active = false;
        set({ kind: 'running', progress: null });
      },
      cancelled: gone,
    });
    if (!wait.active) set({ kind: 'running', progress: null });
    await running;
    set({ kind: 'ready' });
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Enter' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.nativeEvent.isComposing) return;
    if (!(e.target instanceof HTMLElement) || !entersPrimary(e.target, e.currentTarget, e.defaultPrevented)) return;
    e.preventDefault();
    if (phase.kind === 'checkFailed') return;
    void runExport();
  };

  const primary =
    phase.kind === 'checkFailed' ? (
      <Button
        variant="danger"
        icon="TriangleAlert"
        onClick={() => {
          void saveAnyway();
        }}
      >
        {text.t(m.saveAnyway)}
      </Button>
    ) : (
      <Button
        ref={exportRef}
        variant={blocking > 0 ? 'danger' : 'primary'}
        icon={blocking > 0 ? 'TriangleAlert' : 'PackageCheck'}
        disabledReason={busy ? busyReason : null}
        onClick={() => {
          void runExport();
        }}
      >
        {blocking > 0 ? text.t(m.exportAnyway) : text.t(m.export)}
      </Button>
    );

  return (
    <Dialog
      open
      size="lg"
      title={text.t(m.dialogTitle)}
      onClose={cancel}
      footer={
        <div className={s.footer} data-keys="native">
          <Button
            icon="ClipboardCopy"
            className={s.footerStart}
            disabledReason={busy ? busyReason : null}
            onClick={() => {
              void copy();
            }}
          >
            {text.t(m.copyChart)}
          </Button>
          <Button variant="ghost" onClick={cancel}>
            {text.t(m.cancel)}
          </Button>
          {primary}
        </div>
      }
    >
      <div className={s.dialog} data-keys="native" data-region="publish" onKeyDown={onKeyDown}>
        <TextField
          label={text.t(m.fileName)}
          value={shownName}
          maxLength={240}
          onChange={(v) => {
            setTypedName({ target, name: v });
          }}
          onCommit={(v) => {
            setTypedName({ target, name: v });
          }}
        />
        <Segmented<Destination>
          label={text.t(m.destination)}
          value={destination}
          size="sm"
          options={[
            { value: 'download', label: text.t(m.destDownload), icon: 'Download' },
            {
              value: 'saveAs',
              label: text.t(m.destSaveAs),
              icon: 'FolderOpen',
              ...(saver.canChoose ? {} : { disabledReason: { key: m.destSaveAsUnavailable } }),
            },
          ]}
          onChange={(v) => {
            writePref(services.settings, 'destination', v);
          }}
        />
        <div className={s.dialogChoices}>
          <Segmented<PublishTarget>
            label={text.t(m.exportTarget)}
            value={target}
            size="sm"
            options={(['pez', 'chartJson', 'backup'] as const).map((t) => ({
              value: t,
              label: text.tr(TARGET_TEXT[t].label),
            }))}
            onChange={(t) => {
              writePref(services.settings, 'target', t);
            }}
          />
          {target !== 'chartJson' && (
            <Select<ExportProfile>
              label={text.t(m.profile)}
              value={store.doc.pkg.exportProfile}
              options={[
                { value: 'minimal', label: text.t(m.profileMinimal) },
                {
                  value: 'rpe-compatible',
                  label: model.compat.rpe.verified ? text.t(m.profileRpe) : text.t(m.profileRpeUnverified),
                },
              ]}
              onChange={(v) => {
                patchPkg(services, store, 'profile', { exportProfile: v });
              }}
            />
          )}
        </div>
        <span className={s.muted}>{text.tr(TARGET_TEXT[target].help)}</span>
        <Preflight services={services} text={text} model={model} />
        {target === 'chartJson' && <ChartOnlyNotice services={services} text={text} model={model} mode="target" />}
        <Output text={text} model={model} full={full} />
        {target !== 'chartJson' && (
          <ChartOnlyNotice services={services} text={text} model={model} mode="copy" collapsed />
        )}
        {phase.kind === 'checking' && <ProgressBar value={null} label={{ key: m.waitingForCheck }} />}
        {phase.kind === 'running' && <ProgressBar value={phase.progress} label={{ key: m.progressLabel }} />}
        {phase.kind === 'checkFailed' && !phase.warned && (
          <Failure
            services={services}
            text={text}
            title={text.t(m.selfCheckFailedTitle)}
            body={text.t(m.selfCheckFailedBody)}
            diffs={phase.produced.result.selfCheck.diffs}
          />
        )}
        {phase.kind === 'checkFailed' && phase.warned && (
          <Failure
            services={services}
            text={text}
            title={text.t(m.warnedTitle)}
            body={text.t(m.warnedBody)}
            diffs={phase.produced.result.warnings.map((w) => text.tr(w))}
          />
        )}
        {phase.kind === 'empty' && (
          <Failure
            services={services}
            text={text}
            title={text.t(m.emptyTitle)}
            body={text.t(m.emptyBody)}
            diffs={phase.diffs}
          />
        )}
        {phase.kind === 'error' && (
          <Failure services={services} text={text} title={text.t(m.errorTitle)} body={phase.message} diffs={[]} />
        )}
      </div>
    </Dialog>
  );
}

/** `DIALOG.exportDialog` (modal). */
export function ExportDialog(props: DialogProps<'exportDialog'>) {
  const { services } = props;
  const close = (result: { readonly fileName: string } | null): void => {
    props.close(result);
  };
  const store = useStore(services);
  const text = useText(services);
  if (store === null || store.projectId !== props.props.projectId) {
    return (
      <Dialog
        open
        size="sm"
        title={text.t(m.dialogTitle)}
        onClose={() => {
          close(null);
        }}
        footer={
          <Button
            variant="primary"
            onClick={() => {
              close(null);
            }}
          >
            {text.t(m.close)}
          </Button>
        }
      >
        <p>{text.t(m.reasonNoProject)}</p>
      </Dialog>
    );
  }
  return <ExportDialogBody services={services} store={store} quick={props.props.quick} close={close} />;
}
